/**
 * PlanetTexture.js
 *
 * June 2026 — Procedural planetary textures for the system-space view.
 *
 * Builds a deterministic equirectangular (2:1) DynamicTexture per body based on
 * its type, then returns a fully configured StandardMaterial. No external image
 * assets — everything is painted on a canvas so it stays self-contained and
 * reproducible from a seed.
 *
 *   Gas Giant   → turbulent horizontal bands + storm ovals
 *   Ice Giant   → smooth pale-blue bands
 *   Terrestrial → blue oceans + green/brown continents + polar caps + clouds
 *   Ocean       → mostly water with scattered islands
 *   Lava        → dark basalt crust with glowing magma veins (emissive)
 *   Moon/Dwarf  → grey rock with craters + maria (icy tint for ice bodies)
 */

import { DynamicTexture, StandardMaterial, Color3, FresnelParameters } from '@babylonjs/core';

function hashStr(str) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function mkRng(seed) {
  let s = (seed | 0) >>> 0;
  if (!s) s = 0x9e3779b9;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
}

function hexToRgb(hex) {
  const h = (hex || '#7a8aa0').replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function rgb(r, g, b) { return `rgb(${r | 0},${g | 0},${b | 0})`; }
function shade(c, f) { return { r: Math.max(0, Math.min(255, c.r * f)), g: Math.max(0, Math.min(255, c.g * f)), b: Math.max(0, Math.min(255, c.b * f)) }; }
function mix(a, b, t) { return { r: a.r + (b.r - a.r) * t, g: a.g + (b.g - a.g) * t, b: a.b + (b.b - a.b) * t }; }

function paintGasGiant(ctx, w, h, base, rng, pale) {
  ctx.fillStyle = rgb(base.r, base.g, base.b);
  ctx.fillRect(0, 0, w, h);
  const bands = pale ? 9 : 16;
  let y = 0;
  for (let i = 0; i < bands; i++) {
    const bh = (h / bands) * (0.6 + rng() * 0.9);
    const f = pale ? 0.85 + rng() * 0.3 : 0.6 + rng() * 0.8;
    const c = shade(base, f);
    // Wobbly band drawn as a series of vertical slices for turbulence.
    const amp = pale ? 2 : 5 + rng() * 6;
    ctx.fillStyle = rgb(c.r, c.g, c.b);
    for (let x = 0; x < w; x += 4) {
      const off = Math.sin((x / w) * Math.PI * (2 + i) + i) * amp;
      ctx.fillRect(x, y + off, 5, bh);
    }
    y += bh;
    if (y > h) break;
  }
  // Storm ovals
  const storms = pale ? 1 : 2 + Math.floor(rng() * 2);
  for (let s = 0; s < storms; s++) {
    const cx = rng() * w;
    const cy = h * (0.3 + rng() * 0.4);
    const rxr = 12 + rng() * 30;
    const ryr = 6 + rng() * 12;
    const c = shade(base, 1.3 + rng() * 0.4);
    ctx.save();
    ctx.globalAlpha = 0.7;
    ctx.fillStyle = rgb(c.r, c.g, c.b);
    ctx.beginPath();
    ctx.ellipse(cx, cy, rxr, ryr, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}

function paintTerrestrial(ctx, w, h, base, rng) {
  // Ocean base.
  const ocean = { r: 32, g: 70, b: 120 };
  ctx.fillStyle = rgb(ocean.r, ocean.g, ocean.b);
  ctx.fillRect(0, 0, w, h);
  // Continents — clusters of overlapping land blobs.
  const land = mix({ r: 70, g: 110, b: 55 }, base, 0.35);
  const clusters = 4 + Math.floor(rng() * 4);
  for (let c = 0; c < clusters; c++) {
    const ccx = rng() * w;
    const ccy = h * (0.18 + rng() * 0.64);
    const blobs = 8 + Math.floor(rng() * 12);
    for (let b = 0; b < blobs; b++) {
      const bx = ccx + (rng() - 0.5) * 90;
      const by = ccy + (rng() - 0.5) * 50;
      const rr = 8 + rng() * 22;
      const t = rng();
      const col = t > 0.7 ? shade(land, 0.7) : (t > 0.4 ? land : shade(land, 1.2)); // browns/greens
      ctx.fillStyle = rgb(col.r, col.g, col.b);
      ctx.beginPath();
      ctx.arc(bx, by, rr, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  // Polar caps.
  ctx.fillStyle = 'rgba(235,242,250,0.92)';
  ctx.fillRect(0, 0, w, h * (0.05 + rng() * 0.05));
  ctx.fillRect(0, h * (0.92 - rng() * 0.05), w, h);
  // Clouds.
  ctx.save();
  ctx.globalAlpha = 0.28;
  ctx.fillStyle = '#ffffff';
  for (let i = 0; i < 26; i++) {
    const cx = rng() * w;
    const cy = rng() * h;
    const rr = 6 + rng() * 26;
    ctx.beginPath();
    ctx.arc(cx, cy, rr, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

function paintOcean(ctx, w, h, base, rng) {
  const ocean = mix({ r: 24, g: 80, b: 130 }, base, 0.25);
  ctx.fillStyle = rgb(ocean.r, ocean.g, ocean.b);
  ctx.fillRect(0, 0, w, h);
  // Subtle current bands.
  ctx.save();
  ctx.globalAlpha = 0.25;
  for (let i = 0; i < 10; i++) {
    const c = shade(ocean, 0.8 + rng() * 0.5);
    ctx.fillStyle = rgb(c.r, c.g, c.b);
    const y = rng() * h;
    ctx.fillRect(0, y, w, 4 + rng() * 8);
  }
  ctx.restore();
  // Small islands.
  const land = { r: 90, g: 110, b: 70 };
  for (let i = 0; i < 14; i++) {
    ctx.fillStyle = rgb(land.r, land.g, land.b);
    ctx.beginPath();
    ctx.arc(rng() * w, rng() * h, 3 + rng() * 9, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = 'rgba(235,242,250,0.85)';
  ctx.fillRect(0, 0, w, h * 0.05);
  ctx.fillRect(0, h * 0.95, w, h);
}

function paintLava(ctx, w, h, base, rng, emissiveCtx) {
  const crust = { r: 38, g: 22, b: 18 };
  ctx.fillStyle = rgb(crust.r, crust.g, crust.b);
  ctx.fillRect(0, 0, w, h);
  if (emissiveCtx) { emissiveCtx.fillStyle = '#000000'; emissiveCtx.fillRect(0, 0, w, h); }
  // Mottled rock.
  for (let i = 0; i < 120; i++) {
    const c = shade(crust, 0.6 + rng() * 1.0);
    ctx.fillStyle = rgb(c.r, c.g, c.b);
    ctx.beginPath();
    ctx.arc(rng() * w, rng() * h, 3 + rng() * 10, 0, Math.PI * 2);
    ctx.fill();
  }
  // Glowing magma veins.
  const veins = 22 + Math.floor(rng() * 18);
  for (let i = 0; i < veins; i++) {
    let x = rng() * w;
    let y = rng() * h;
    const segs = 5 + Math.floor(rng() * 8);
    const hot = rng() > 0.5 ? '#ff8a2a' : '#ffd23a';
    for (const c of [ctx, emissiveCtx]) {
      if (!c) continue;
      c.strokeStyle = hot;
      c.lineWidth = 1.5 + rng() * 2.5;
      c.beginPath();
      c.moveTo(x, y);
    }
    let cx = x; let cy = y;
    for (let sX = 0; sX < segs; sX++) {
      cx += (rng() - 0.5) * 40;
      cy += (rng() - 0.5) * 30;
      if (ctx) ctx.lineTo(cx, cy);
      if (emissiveCtx) emissiveCtx.lineTo(cx, cy);
    }
    if (ctx) ctx.stroke();
    if (emissiveCtx) emissiveCtx.stroke();
  }
}

function paintRocky(ctx, w, h, base, rng, icy) {
  const ground = icy ? mix({ r: 170, g: 190, b: 210 }, base, 0.3) : mix({ r: 120, g: 116, b: 110 }, base, 0.4);
  ctx.fillStyle = rgb(ground.r, ground.g, ground.b);
  ctx.fillRect(0, 0, w, h);
  // Maria (large darker basins).
  for (let i = 0; i < 5; i++) {
    const c = shade(ground, 0.7);
    ctx.save();
    ctx.globalAlpha = 0.6;
    ctx.fillStyle = rgb(c.r, c.g, c.b);
    ctx.beginPath();
    ctx.arc(rng() * w, rng() * h, 18 + rng() * 36, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
  // Craters: dark floor + light rim.
  const craters = 60 + Math.floor(rng() * 60);
  for (let i = 0; i < craters; i++) {
    const cx = rng() * w;
    const cy = rng() * h;
    const rr = 2 + rng() * 12;
    const rim = shade(ground, 1.25);
    ctx.strokeStyle = rgb(rim.r, rim.g, rim.b);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(cx, cy, rr, 0, Math.PI * 2);
    ctx.stroke();
    const floor = shade(ground, 0.7);
    ctx.fillStyle = rgb(floor.r, floor.g, floor.b);
    ctx.beginPath();
    ctx.arc(cx, cy, rr * 0.7, 0, Math.PI * 2);
    ctx.fill();
  }
}

/**
 * Create a fully-configured planetary material for a space body.
 * @param {Scene} scene
 * @param {object} body { name, type, kind, color, planetIdx, moonIdx }
 */
export function createPlanetMaterial(scene, body) {
  const type = (body.type || '').toLowerCase();
  const isMoon = body.kind === 'moon';
  const seed = hashStr(`${body.name || ''}|${body.planetIdx}|${body.moonIdx ?? 'p'}|${body.type || ''}`);
  const rng = mkRng(seed);
  const base = hexToRgb(body.color);

  const big = !isMoon;
  const W = big ? 512 : 256;
  const H = big ? 256 : 128;

  const tex = new DynamicTexture(`planetTex_${body.planetIdx}_${body.moonIdx ?? 'p'}`, { width: W, height: H }, scene, true);
  const ctx = tex.getContext();

  const mat = new StandardMaterial(`planetMat_${body.planetIdx}_${body.moonIdx ?? 'p'}`, scene);
  mat.specularColor = new Color3(0.08, 0.08, 0.1);
  mat.specularPower = 32;

  let emissiveTex = null;
  let emissiveCtx = null;

  if (type.includes('gas')) {
    paintGasGiant(ctx, W, H, base, rng, false);
  } else if (type.includes('ice') && !isMoon) {
    paintGasGiant(ctx, W, H, mix(base, { r: 200, g: 225, b: 240 }, 0.4), rng, true);
  } else if (type.includes('lava') || type.includes('volcan')) {
    emissiveTex = new DynamicTexture(`planetEmis_${body.planetIdx}_${body.moonIdx ?? 'p'}`, { width: W, height: H }, scene, true);
    emissiveCtx = emissiveTex.getContext();
    paintLava(ctx, W, H, base, rng, emissiveCtx);
  } else if (type.includes('ocean') || type.includes('water')) {
    paintOcean(ctx, W, H, base, rng);
    mat.specularColor = new Color3(0.4, 0.45, 0.5);
    mat.specularPower = 64;
  } else if (type.includes('terrestrial') || type.includes('earth') || type.includes('terra')) {
    paintTerrestrial(ctx, W, H, base, rng);
    mat.specularColor = new Color3(0.2, 0.25, 0.3);
  } else {
    // Moons, dwarfs, barren / rocky worlds.
    paintRocky(ctx, W, H, base, rng, type.includes('ice') || type.includes('frozen'));
  }

  tex.update(false);
  mat.diffuseTexture = tex;
  // Soft self-illumination so the night side isn't pitch black.
  mat.emissiveColor = new Color3(base.r / 255, base.g / 255, base.b / 255).scale(0.08);

  if (emissiveTex) {
    emissiveCtx.canvas && emissiveTex.update(false);
    mat.emissiveTexture = emissiveTex;
    mat.emissiveColor = new Color3(1, 0.9, 0.7);
  } else {
    // Atmospheric rim-glow — the planet's silhouette catches a soft coloured
    // halo when viewed from space (Fresnel falloff toward the edge). Bright at
    // grazing angles, near-zero toward the camera-facing centre so the lit face
    // keeps its painted detail.
    const atmo = (type.includes('terrestrial') || type.includes('ocean') || type.includes('earth') || type.includes('terra'))
      ? new Color3(0.35, 0.55, 0.95)
      : new Color3(base.r / 255, base.g / 255, base.b / 255).scale(1.3);
    mat.emissiveColor = atmo;
    const rim = new FresnelParameters();
    rim.bias = 0.15;
    rim.power = 2.6;
    rim.leftColor = new Color3(1, 1, 1);
    rim.rightColor = new Color3(0.08, 0.08, 0.08);
    mat.emissiveFresnelParameters = rim;
  }

  return mat;
}

export default createPlanetMaterial;
